import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TimeInput } from 'semantic-ui-calendar-react';
import { Form } from 'semantic-ui-react';
import MilkMoneyAccountingService, { TourDeliveryType } from '~/api/services/milkMoneyAccounting';
import Drawer from '~/components/Drawer';
import { FormFields } from '~/components/FormFields';
import notify from '~/components/Notify';
import { useAuth } from '~/routes/components/AuthProvider';
import { DrawerFooter, StyledErrorLabel } from './style';
import { GlobalEl } from '~/styles/app';
import { handleErrors } from '~/utils/helpers';

interface AddEditMilkDeliveryProps {
  isVisible: boolean;
  idMilkDelivery?: string;
  handleHiddenDrawer: (shouldUpdate?) => void;
}

const defaultValues = {
  supplierNo: '',
  amount: '',
  avgTemp: '',
  timeFrom: '',
  timeTo: '',
  phValue: ''
}

const AddEditMilkDelivery: React.FC<AddEditMilkDeliveryProps> = (props) => {
  const { isVisible, idMilkDelivery, handleHiddenDrawer } = props;
  const { t } = useTranslation();
  const { id } = useParams()
  const [isBusy, setIsBusy] = useState(false)
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const { handleSubmit, control, reset, setValue, watch } = useForm<TourDeliveryType>({
    mode: 'onSubmit'
  });

  const watchTimeFrom = watch('timeFrom')
  const watchTimeTo = watch('timeTo')

  useEffect(() => {
    if (idMilkDelivery && idMilkDelivery !== 'create') {
      fetchMilkDeliveryData()
      return
    }
    reset(defaultValues)
  }, [idMilkDelivery])

  async function fetchMilkDeliveryData() {
    setIsBusy(true)
    try {
      const response = await MilkMoneyAccountingService.getMilkDeliveryData(id, idMilkDelivery)
      reset(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function onSubmit(data: TourDeliveryType) {
    setIsBusy(true)
    data.avgTemp = data.avgTemp.toString().replace(',', '.')
    data.amount = data?.amount?.toString()?.replace(',', '.')
    try {
      if (idMilkDelivery === 'create') {
        await MilkMoneyAccountingService.storeMilkDeliveryData(id, data)
        notify(t('Milk delivery created'), 'success')
      } else {
        await MilkMoneyAccountingService.updateMilkDeliveryData(id, idMilkDelivery, data)
        notify(t('Milk delivery updated'), 'success')
      }
      handleHiddenDrawer(true);
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function convertToMinutes(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  function renderContent() {
    return <>
      <FormFields.Input
        control={control}
        name={'supplierNo'}
        label={t('Supplier no.')}
        placeholder={t('Enter supplier no.')}
        rules={{
          required: {
            value: true,
            message: t('Please enter supplier no.')
          },
          maxLength: {
            value: 12,
            message: t('Max number of char. is 12')
          }
        }}
      />
      <FormFields.Input
        control={control}
        name={'amount'}
        label={t('Amount accepted (in l)')}
        placeholder={t('Enter amount accepted (in l)')}
        rules={{
          required: {
            value: true,
            message: t('Please enter amount accepted (in l)')
          }
        }}
        decimalScale={1}
        typeInput={'controled'}
        allowDecimals
      />
      <FormFields.Input
        control={control}
        name={'avgTemp'}
        label={t('avg. Temp. (°C)')}
        placeholder={t('Enter avg. Temp.')}
        rules={{
          required: {
            value: true,
            message: t('Please enter avg. Temp.')
          },
          max: {
            value: 99.9,
            message: t('Avg temp may not be greater than 99.9.')
          }
        }}
        decimalScale={1}
        allowDecimals
        typeInput={'controled'}
      />
      <Controller
        control={control}
        name={'timeFrom'}
        rules={{
          required: {
            value: true,
            message: t('Please enter time from')
          }
        }}
        render={({ field, fieldState }) => {
          return <Form.Field required>
            <label>{t('Time from')}</label>
            <TimeInput
              placeholder={t('Time')}
              closable
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              value={watchTimeFrom || ''}
              iconPosition='left'
              onChange={(event, { name, value }) => {
                event.preventDefault()
                event.stopPropagation()
                setValue('timeFrom', value)
              }
              }
            />
            {fieldState.error && <StyledErrorLabel className='ui pointing basic label'>{fieldState?.error?.message}</StyledErrorLabel>}
          </Form.Field>
        }}
      />
      <Controller
        control={control}
        name={'timeTo'}
        render={() => {
          return <Form.Field>
            <label>{t('Time to')}</label>
            <TimeInput
              placeholder={t('Time')}
              disabled={!watchTimeFrom}
              onBlur={() => {
                const selectedTimeInMinutes = convertToMinutes(watchTimeTo);
                const minTimeInMinutes = convertToMinutes(watchTimeFrom);
                if (selectedTimeInMinutes < minTimeInMinutes) {
                  setValue('timeTo', '')
                  notify(t('Time to is ahead time from'), 'error')
                  return
                }
              }}
              closable
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              value={watchTimeTo || ''}
              iconPosition='left'
              onChange={(event, { name, value }) => {
                event.preventDefault()
                event.stopPropagation()
                setValue('timeTo', value)
              }}
            />
          </Form.Field>
        }}
      />
      <DrawerFooter>
        <GlobalEl.DefaultOutlineButton type='button' onClick={() => handleHiddenDrawer()}>
          {t('Cancel')}
        </GlobalEl.DefaultOutlineButton>
        <GlobalEl.PrimaryButton
        >{idMilkDelivery === 'create' ? t('Add') : t('Update')}</GlobalEl.PrimaryButton>
      </DrawerFooter>
    </>
  }

  return <Drawer
    onHidden={() => {
      reset();
      handleHiddenDrawer();
    }}
    handleSubmit={handleSubmit(onSubmit)}
    isBusy={isBusy}
    header={idMilkDelivery === 'create' ? t('Add additional milk collection (farmer)') : t('Edit additional milk collection (farmer)')}
    content={renderContent()}
    isVisible={isVisible}
    closeOnEscape
  />
}
export default AddEditMilkDelivery